import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import SearchTabs from "@/components/home/SearchTabs";
import AISearchBar from "@/components/AISearchBar";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";
import * as LucideIcons from "lucide-react";
import { Award, Shield, ShieldCheck, Users, Ticket, Hotel, Plane, Car, Mountain, Star, Quote, Compass, Bus, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PromoBannerCarousel } from "@/components/PromoBanner";

const CATEGORIES = [
  { id: "tour", label: "SIC Tours", icon: Bus, color: "bg-[#E5F0F0] text-teal", desc: "Daily departing coach tours", link: "/tours?sub_category=SIC" },
  { id: "ticket", label: "Entry Tickets", icon: Ticket, color: "bg-[#FFF1E6] text-saffron", desc: "RFC, Wonderla, Snow World", link: "/tickets" },
  { id: "hotel", label: "Hotels & Resorts", icon: Hotel, color: "bg-[#E5F0F0] text-teal", desc: "Staycations & weekend stays", link: "/hotels" },
  { id: "cab", label: "Cabs & Transport", icon: Car, color: "bg-[#FFF1E6] text-saffron", desc: "Airport, outstation, hire", link: "/cabs" },
  { id: "activity", label: "Adventure", icon: Mountain, color: "bg-[#E5F0F0] text-teal", desc: "Trekking, camping, climbing", link: "/adventure" },
  { id: "int", label: "International", icon: Plane, color: "bg-[#FFF1E6] text-saffron", desc: "Dubai, Thailand, Bali & more", link: "/search?sub_category=INTERNATIONAL" },
];

export default function Home() {
  const { settings } = useSettings();
  const [featured, setFeatured] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [adventures, setAdventures] = useState([]);
  const [staycations, setStaycations] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data));
    api.get("/products", { params: { category: "ticket", limit: 4 } }).then((r) => setTickets(r.data));
    api.get("/products", { params: { category: "activity", limit: 4 } }).then((r) => setAdventures(r.data));
    api.get("/products", { params: { sub_category: "staycation", limit: 4 } }).then((r) => setStaycations(r.data));
    api.get("/destinations/popular").then((r) => setDestinations(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/blog", { params: { limit: 3 } }).then((r) => setBlogPosts(r.data));
  }, []);

  const testimonials = settings.testimonials || [];
  const heroImg = settings.hero_image || "https://images.unsplash.com/photo-1696941515998-d83f24967aca?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";
  const siteName = settings.site_name || "HyderabadTourism.in";
  const siteUrl = settings.site_url || (typeof window !== "undefined" ? window.location.origin : "");

  return (
    <div data-testid="home-page">
      <SEO
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "TravelAgency",
            "name": siteName,
            "url": siteUrl,
            "logo": settings.logo_url || settings.seo_og_image,
            "image": settings.seo_og_image,
            "description": settings.seo_meta_description || "Trusted Hyderabad travel partner since 2005 — tours, hotels, tickets, cabs & adventures. GST-compliant, Razorpay-secured.",
            "foundingDate": "2005-01-01",
            "priceRange": "₹₹",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": settings.contact_address || "KPHB Colony, Hyderabad",
              "addressLocality": "Hyderabad",
              "addressRegion": "Telangana",
              "postalCode": "500073",
              "addressCountry": "IN",
            },
            "telephone": settings.contact_phone,
            "email": settings.contact_email,
            "sameAs": [
              settings.social_facebook,
              settings.social_instagram,
              settings.social_youtube,
              settings.social_twitter,
              settings.social_linkedin,
            ].filter(Boolean),
            "areaServed": ["Hyderabad", "Telangana", "India"],
            "makesOffer": [
              { "@type": "OfferCatalog", "name": "Hyderabad Tours" },
              { "@type": "OfferCatalog", "name": "Hotels & Staycations" },
              { "@type": "OfferCatalog", "name": "Attraction Entry Tickets" },
              { "@type": "OfferCatalog", "name": "Cabs & Airport Transfers" },
              { "@type": "OfferCatalog", "name": "Adventure & Camping" },
              { "@type": "OfferCatalog", "name": "International Holiday Packages" },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": siteName,
            "url": siteUrl,
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${siteUrl}/search?q={search_term_string}`,
              },
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": siteName,
            "url": siteUrl,
            "logo": settings.logo_url || settings.seo_og_image,
            "foundingDate": "2005-01-01",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": settings.contact_phone,
              "contactType": "customer service",
              "areaServed": "IN",
              "availableLanguage": ["English", "Telugu", "Hindi"],
            },
          },
        ]}
      />
      {/* HERO */}
      <section className="relative">
        <div className="relative min-h-[560px] md:h-[600px]">
          <img src={heroImg} alt="Hyderabad" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-14 pb-8 md:pb-10 flex flex-col justify-start">
            <div className="max-w-3xl">
              <div className="inline-block bg-saffron/90 text-white text-xs uppercase tracking-widest px-3 py-1.5 rounded-full mb-4 font-semibold">
                {settings.hero_badge || "Trusted by 10,000+ travellers since 2005"}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white font-bold leading-tight mb-3">
                {settings.hero_title_line1 || "Discover Hyderabad."} <br className="hidden sm:block" />
                <span className="text-cyan-bright">{settings.hero_title_line2 || "Explore beyond."}</span>
              </h1>
              <p className="text-sm sm:text-base text-white/90 mb-5 max-w-2xl line-clamp-2 sm:line-clamp-none">
                {settings.hero_subtitle || "From Ramoji Film City to Kashmir valleys — book tours, hotels, tickets, cabs and adventures all in one place."}
              </p>
              <div className="flex flex-wrap gap-3 mb-4">
                <Link to="/enquire">
                  <Button className="bg-saffron hover:bg-saffron-dark text-white shadow-lg h-11 px-6 text-sm font-semibold" data-testid="hero-enquire-btn">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Enquire Now
                  </Button>
                </Link>
                <Link to="/search">
                  <Button variant="outline" className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 h-11 px-6 text-sm font-semibold" data-testid="hero-explore-btn">
                    Explore Packages
                  </Button>
                </Link>
              </div>
            </div>
            <div className="mt-2 md:mt-4">
              <SearchTabs />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-3" data-testid="trust-est">
            <Award className="w-8 h-8 text-saffron" />
            <div><div className="font-semibold">{settings.trust_badges?.badge1_title || `Est. ${settings.trust_badges?.est_year || "2005"}`}</div><div className="text-xs text-muted-foreground">{settings.trust_badges?.badge1_sub || "20+ years of expertise"}</div></div>
          </div>
          <div className="flex items-center gap-3" data-testid="trust-gst">
            <Shield className="w-8 h-8 text-saffron" />
            <div><div className="font-semibold">{settings.trust_badges?.badge2_title || "GST Compliant"}</div><div className="text-xs text-muted-foreground">{settings.trust_badges?.badge2_sub || "Proper tax invoices"}</div></div>
          </div>
          <div className="flex items-center gap-3" data-testid="trust-secure">
            <ShieldCheck className="w-8 h-8 text-saffron" />
            <div><div className="font-semibold">{settings.trust_badges?.badge3_title || "100% Secure"}</div><div className="text-xs text-muted-foreground">{settings.trust_badges?.badge3_sub || "Razorpay protected"}</div></div>
          </div>
          <div className="flex items-center gap-3" data-testid="trust-customers">
            <Users className="w-8 h-8 text-saffron" />
            <div><div className="font-semibold">{settings.trust_badges?.badge4_title || `${settings.trust_badges?.happy_customers || "10,000+"} Travellers`}</div><div className="text-xs text-muted-foreground">{settings.trust_badges?.badge4_sub || "Happy customers"}</div></div>
          </div>
        </div>
      </section>

      {/* PROMO BANNERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <PromoBannerCarousel position="homepage" />
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-xs uppercase tracking-[0.2em] text-teal font-semibold mb-2">What we offer</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-3 leading-tight">Plan every part of your trip</h2>
        <p className="text-muted-foreground max-w-2xl mb-8">From single-attraction tickets to full international holidays — one trusted booking partner for everything.</p>

        <div className="mb-10">
          <AISearchBar />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {(categories.filter((c) => !c.parent_id).length > 0 ? categories.filter((c) => !c.parent_id) : CATEGORIES).map((c, idx) => {
            const iconName = String(c.icon || "");
            const toPascal = (s) => s.replace(/(^|[-_ ])(\w)/g, (_, __, ch) => ch.toUpperCase());
            const Icon = LucideIcons[iconName]
              || (iconName && LucideIcons[iconName.charAt(0).toUpperCase() + iconName.slice(1)])
              || (iconName && LucideIcons[toPascal(iconName)])
              || LucideIcons.Compass;
            const link = `/search?category=${c.slug || c.id}`;
            const label = c.name || c.label;
            const desc = c.description || c.desc;
            const colorClass = idx % 2 === 0 ? "bg-[#E5F0F0] text-teal" : "bg-[#FFF1E6] text-saffron";
            return (
              <Link
                key={c.id || c.slug}
                to={link}
                className="bg-white border border-border rounded-2xl p-5 card-hover group"
                data-testid={`category-${c.slug || c.id}`}
              >
                <div className={`w-12 h-12 rounded-xl ${colorClass} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="font-semibold text-foreground mb-1">{label}</div>
                <div className="text-xs text-muted-foreground">{desc}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED TOURS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-teal font-semibold mb-2">Most loved</div>
            <h2 className="font-display text-3xl sm:text-4xl">Featured tours & experiences</h2>
          </div>
          <Link to="/search" className="text-teal hover:text-saffron text-sm font-medium hidden sm:block" data-testid="view-all-tours">View all →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.slice(0, 8).map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* STAYCATIONS */}
      {staycations.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-teal font-semibold mb-2">Weekend escapes</div>
              <h2 className="font-display text-3xl sm:text-4xl">Staycations near Hyderabad</h2>
              <p className="text-muted-foreground mt-2 max-w-xl">Forest, lake and heritage getaways — perfect for a short breather.</p>
            </div>
            <Link to="/hotels?sub_category=staycation" className="text-teal hover:text-saffron text-sm font-medium hidden sm:block" data-testid="view-all-staycations">All staycations →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {staycations.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* ENTRY TICKETS QUICK BOOK */}
      <section className="bg-gradient-to-b from-[#E5F0F0] to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-saffron font-semibold mb-2">Skip the queue</div>
              <h2 className="font-display text-3xl sm:text-4xl">Entry Tickets — Instant Booking</h2>
              <p className="text-muted-foreground mt-2">Confirmed e-tickets delivered on WhatsApp & email instantly.</p>
            </div>
            <Link to="/tickets" className="text-teal hover:text-saffron text-sm font-medium hidden sm:block">All tickets →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tickets.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      {destinations.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-teal font-semibold mb-2">Popular destinations</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-8">Where would you like to go?</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {destinations.slice(0, 8).map((d) => (
              <Link
                key={d.name}
                to={`/search?destination=${encodeURIComponent(d.name)}`}
                className="group relative aspect-[4/3] rounded-2xl overflow-hidden card-hover"
                data-testid={`destination-${d.name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#041A1A]/85 via-[#041A1A]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <div className="font-display text-xl font-semibold">{d.name}</div>
                  <div className="text-xs opacity-80">{d.count} packages</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ADVENTURE */}
      <section className="bg-[#041A1A] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-saffron font-semibold mb-2">New on the platform</div>
              <h2 className="font-display text-3xl sm:text-4xl text-white">Adventure, camping & trekking</h2>
              <p className="text-white/70 mt-2 max-w-xl">Weekend escapes from Hyderabad — trekking, stargazing, climbing & water sports.</p>
            </div>
            <Link to="/adventure" className="text-saffron hover:text-white text-sm font-medium hidden sm:block">Explore →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {adventures.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.2em] text-teal font-semibold mb-2">Travellers say</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-10">Real reviews. Real journeys.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white border border-border rounded-2xl p-7 card-hover" data-testid={`testimonial-${i}`}>
              <Quote className="w-8 h-8 text-saffron/40 mb-3" />
              <div className="flex gap-0.5 mb-3">
                {[...Array(t.rating)].map((_, j) => <Star key={j} className="w-4 h-4 fill-saffron text-saffron" />)}
              </div>
              <p className="text-foreground mb-5 leading-relaxed">"{t.text}"</p>
              <div className="font-semibold">{t.name}</div>
              <div className="text-xs text-muted-foreground">{t.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BLOG SECTION */}
      {blogPosts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-teal font-semibold mb-2">From the blog</div>
              <h2 className="font-display text-3xl sm:text-4xl">Travel guides & insider tips</h2>
            </div>
            <Link to="/blog" className="text-teal hover:text-saffron text-sm font-medium hidden sm:block" data-testid="home-view-blog">All articles →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {blogPosts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="bg-white border border-border rounded-2xl overflow-hidden card-hover group" data-testid={`home-blog-${p.slug}`}>
                {p.cover_image && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-display text-lg font-semibold line-clamp-2 mb-2 group-hover:text-teal transition-colors">{p.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>
                  <div className="mt-3 text-sm text-teal font-medium">Read article →</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-teal rounded-3xl p-10 sm:p-14 text-center text-white relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-saffron/20" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-saffron/10" />
          <div className="relative z-10">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-4">{settings.cta_title || "Custom tour? We're here."}</h2>
            <p className="text-white/90 mb-6 max-w-xl mx-auto">{settings.cta_subtitle || "Tell us your group size, budget and dates — we'll craft a tailored itinerary with stay, transport and activities."}</p>
            <Button className="bg-saffron hover:bg-saffron-dark text-white text-base font-semibold px-8 py-6 rounded-xl" data-testid="home-cta-enquire">
              <a href={`https://wa.me/${(settings.whatsapp_number || "919000000000").replace(/\D/g, "")}`} target="_blank" rel="noreferrer">Enquire on WhatsApp</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
